import {
  User,
  Stats,
  PointTransactionListResponse,
  PointTransactionStats,
  ApiResponse,
} from '../types';
import { createApiInstance } from './auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// 인증 헤더 생성
const getAuthHeaders = () => {
  const token = localStorage.getItem('admin_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

// 동적 토큰을 사용하는 API 인스턴스
const getApi = () => createApiInstance();

// 사용자 관련 API
export const userApi = {
  // 사용자 목록 조회
  getUsers: async (
    page = 1,
    limit = 20,
    search = ''
  ): Promise<{
    users: User[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (search) params.append('search', search);

    const response = await fetch(`${API_BASE_URL}/admin/users?${params}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch users');
    }

    const data = await response.json();
    return data.data;
  },

  // 사용자 상세 조회
  getUser: async (userId: string): Promise<ApiResponse<{ user: User }>> => {
    const api = getApi();
    const response = await api.get(`/admin/users/${userId}`);
    return response.data;
  },

  // 사용자 포인트 추가
  addUserPoints: async (userId: string, amount: number, reason?: string): Promise<ApiResponse<any>> => {
    const api = getApi();
    const response = await api.post(`/admin/users/${userId}/points/add`, {
      amount,
      reason,
    });
    return response.data;
  },

  // 사용자 포인트 차감
  deductUserPoints: async (userId: string, amount: number, reason?: string): Promise<ApiResponse<any>> => {
    const api = getApi();
    const response = await api.post(`/admin/users/${userId}/points/deduct`, {
      amount,
      reason,
    });
    return response.data;
  },

  // 사용자 삭제
  deleteUser: async (userId: string): Promise<ApiResponse<any>> => {
    const api = getApi();
    const response = await api.delete(`/admin/users/${userId}`);
    return response.data;
  },

  // 사용자별 관상 분석 이력 조회
  getUserAnalyses: async (
    userId: string,
    page = 1,
    limit = 20,
    sortBy = 'createdAt',
    sortOrder = 'desc'
  ): Promise<ApiResponse<any>> => {
    const api = getApi();
    const response = await api.get(
      `/admin/users/${userId}/analyses?page=${page}&limit=${limit}&sortBy=${sortBy}&sortOrder=${sortOrder}`
    );
    return response.data;
  },
};

// 통계 관련 API
export const statsApi = {
  // 전체 통계 조회
  getStats: async (): Promise<ApiResponse<Stats>> => {
    const api = getApi();
    const response = await api.get('/admin/stats');
    return response.data;
  },
};

// 포인트 관리 관련 API
export const pointApi = {
  // 사용자 포인트 지급
  addPoints: async (userId: string, amount: number, reason?: string): Promise<ApiResponse<any>> => {
    const api = getApi();
    const response = await api.post('/admin/users/points', {
      userId,
      amount,
      reason,
    });
    return response.data;
  },

  // 사용자 포인트 차감
  deductPoints: async (userId: string, amount: number, reason?: string): Promise<ApiResponse<any>> => {
    const api = getApi();
    const response = await api.post('/admin/users/points/deduct', {
      userId,
      amount,
      reason,
    });
    return response.data;
  },

  // 사용자 포인트 내역 조회
  getPointHistory: async (userId: string, page = 1, limit = 20): Promise<ApiResponse<any>> => {
    const api = getApi();
    const response = await api.get(`/admin/users/${userId}/points/history?page=${page}&limit=${limit}`);
    return response.data;
  },
};

// 포인트 거래 내역 관련 API
export const pointTransactionApi = {
  // 전체 포인트 거래 내역 조회
  getTransactions: async (
    page = 1,
    limit = 20,
    userId?: string,
    type?: string,
    startDate?: string,
    endDate?: string,
    sortBy = 'createdAt',
    sortOrder = -1
  ): Promise<ApiResponse<PointTransactionListResponse>> => {
    const api = getApi();
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      sortBy,
      sortOrder: sortOrder.toString(),
    });

    if (userId) params.append('userId', userId);
    if (type) params.append('type', type);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.get(`/admin/point-transactions?${params.toString()}`);
    return response.data;
  },

  // 특정 사용자의 포인트 거래 내역 조회
  getUserTransactions: async (
    userId: string,
    page = 1,
    limit = 20,
    type?: string,
    startDate?: string,
    endDate?: string,
    sortBy = 'createdAt',
    sortOrder = -1
  ): Promise<ApiResponse<PointTransactionListResponse>> => {
    const api = getApi();
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      sortBy,
      sortOrder: sortOrder.toString(),
    });

    if (type) params.append('type', type);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.get(`/admin/users/${userId}/point-transactions?${params.toString()}`);
    return response.data;
  },

  // 관리자별 포인트 거래 내역 조회
  getAdminTransactions: async (
    adminId?: string,
    page = 1,
    limit = 20,
    type?: string,
    startDate?: string,
    endDate?: string,
    sortBy = 'createdAt',
    sortOrder = -1
  ): Promise<ApiResponse<PointTransactionListResponse>> => {
    const api = getApi();
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      sortBy,
      sortOrder: sortOrder.toString(),
    });

    if (adminId) params.append('adminId', adminId);
    if (type) params.append('type', type);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.get(`/admin/admin-point-transactions?${params.toString()}`);
    return response.data;
  },

  // 포인트 거래 통계 조회
  getTransactionStats: async (
    userId?: string,
    adminId?: string,
    startDate?: string,
    endDate?: string,
    type?: string
  ): Promise<ApiResponse<PointTransactionStats>> => {
    const api = getApi();
    const params = new URLSearchParams();

    if (userId) params.append('userId', userId);
    if (adminId) params.append('adminId', adminId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (type) params.append('type', type);

    const response = await api.get(`/admin/point-transaction-stats?${params.toString()}`);
    return response.data;
  },

  // 포인트 거래 내역 내보내기
  exportTransactions: async (
    userId?: string,
    type?: string,
    startDate?: string,
    endDate?: string,
    format = 'csv'
  ): Promise<Blob> => {
    const api = getApi();
    const params = new URLSearchParams({ format });

    if (userId) params.append('userId', userId);
    if (type) params.append('type', type);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.get(`/admin/point-transactions/export?${params.toString()}`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

export default getApi;
